import { z } from 'zod';
import type { CustomerToken } from '@sleed/bizcommerce-api';

/**
 * Customer edit form. Mirrors `CustomerToken` and the legacy admin editor
 * (Core/client/admin/src/pages/Customers/Customers/index.js). Covers the well-understood account,
 * classification, commercial, company, flag, ERP and image fields. Fields not surfaced here
 * (social ids, loyalty, referral, login devices, the ambiguous tax-status `ts`) are carried through
 * unchanged on update by spreading the original token under the form-mapped fields, so an edit never
 * wipes them. `psw` is required only on create.
 */
const optString = z.string().trim().nullable().optional();

export const customerSchema = z.object({
	id: z.number().optional(),
	// Account
	eml: z.string().trim().min(1, 'validation.required').max(50, 'validation.tooLong').email('validation.email'),
	UserAlias: z.string().trim().max(255, 'validation.tooLong').nullable().optional(),
	psw: optString,
	fn: z.string().trim().max(50, 'validation.tooLong').nullable().optional(),
	ln: z.string().trim().max(50, 'validation.tooLong').nullable().optional(),
	act: z.boolean(),
	cfm: z.boolean(),
	// Classification
	ccids: z.array(z.number()),
	c_tier_id: z.number().nullable().optional(),
	gd: z.number().nullable().optional(),
	dob: optString,
	dsc: z.number().nullable().optional(),
	// Commercial
	prid: z.number().nullable().optional(),
	pmid: z.number().nullable().optional(),
	shid: z.number().nullable().optional(),
	tsid: z.number().nullable().optional(),
	// Company
	cnm: z.string().trim().max(100, 'validation.tooLong').nullable().optional(),
	dtl: z.string().trim().max(100, 'validation.tooLong').nullable().optional(),
	vat: z.string().trim().max(20, 'validation.tooLong').nullable().optional(),
	irs: z.string().trim().max(50, 'validation.tooLong').nullable().optional(),
	tel: z.string().trim().max(20, 'validation.tooLong').nullable().optional(),
	mob: z.string().trim().max(20, 'validation.tooLong').nullable().optional(),
	fax: z.string().trim().max(20, 'validation.tooLong').nullable().optional(),
	// Flags
	rnl: z.boolean().optional(),
	popt: z.boolean().optional(),
	rpm: z.boolean().optional(),
	hidden_reader: z.boolean().optional(),
	lang: optString,
	// ERP
	erpcd: optString,
	erpcd2: optString,
	erpcd3: optString,
	erpcd4: optString,
	erpcd5: optString,
	// Image
	img1: z.string().nullable().optional(),
	imgdel1: z.boolean().optional(),
	// Staged file (uploaded out-of-band against the saved id; never serialised into the token).
	img1File: z.instanceof(File).nullable().optional()
});

export type CustomerFormValues = z.infer<typeof customerSchema>;

export const EMPTY_CUSTOMER: CustomerFormValues = {
	eml: '',
	UserAlias: '',
	psw: '',
	fn: '',
	ln: '',
	act: true,
	cfm: false,
	ccids: [],
	c_tier_id: null,
	gd: null,
	dob: null,
	dsc: null,
	prid: null,
	pmid: null,
	shid: null,
	tsid: null,
	cnm: '',
	dtl: '',
	vat: '',
	irs: '',
	tel: '',
	mob: '',
	fax: '',
	rnl: false,
	popt: false,
	rpm: false,
	hidden_reader: false,
	lang: null,
	erpcd: '',
	erpcd2: '',
	erpcd3: '',
	erpcd4: '',
	erpcd5: '',
	img1: null,
	imgdel1: false,
	img1File: null
};

export function tokenToForm(token: CustomerToken): CustomerFormValues {
	return {
		id: token.id,
		eml: token.eml ?? '',
		UserAlias: token.UserAlias ?? '',
		psw: '',
		fn: token.fn ?? '',
		ln: token.ln ?? '',
		act: token.act ?? false,
		cfm: token.cfm ?? false,
		ccids: token.ccids ?? [],
		c_tier_id: token.c_tier_id ?? null,
		gd: token.gd ?? null,
		dob: token.dob ? String(token.dob) : null,
		dsc: token.dsc ?? null,
		prid: token.prid ?? null,
		pmid: token.pmid ?? null,
		shid: token.shid ?? null,
		tsid: token.tsid ?? null,
		cnm: token.cnm ?? '',
		dtl: token.dtl ?? '',
		vat: token.vat ?? '',
		irs: token.irs ?? '',
		tel: token.tel ?? '',
		mob: token.mob ?? '',
		fax: token.fax ?? '',
		rnl: token.rnl ?? false,
		popt: token.popt ?? false,
		rpm: token.rpm ?? false,
		hidden_reader: token.hidden_reader ?? false,
		lang: token.lang ?? null,
		erpcd: token.erpcd ?? '',
		erpcd2: token.erpcd2 ?? '',
		erpcd3: token.erpcd3 ?? '',
		erpcd4: token.erpcd4 ?? '',
		erpcd5: token.erpcd5 ?? '',
		img1: token.img1 ?? null,
		imgdel1: false,
		img1File: null
	};
}

/**
 * Map the form back to a token. On update, the caller spreads the original token first
 * (`{ ...original, ...formToToken(form) }`) so server-owned fields not on the form survive.
 */
export function formToToken(form: CustomerFormValues): CustomerToken {
	const trimmed = (v: string | null | undefined) => (v?.trim() ? v.trim() : null);
	return {
		id: form.id,
		eml: form.eml.trim(),
		UserAlias: trimmed(form.UserAlias),
		// Only send a password when one was entered (avoids overwriting the hash with a blank).
		...(form.psw?.trim() ? { psw: form.psw.trim() } : {}),
		fn: trimmed(form.fn),
		ln: trimmed(form.ln),
		act: form.act,
		cfm: form.cfm,
		ccids: form.ccids,
		c_tier_id: form.c_tier_id ?? null,
		gd: form.gd ?? undefined,
		dob: trimmed(form.dob),
		dsc: form.dsc ?? null,
		prid: form.prid ?? null,
		pmid: form.pmid ?? null,
		shid: form.shid ?? null,
		tsid: form.tsid ?? null,
		cnm: trimmed(form.cnm),
		dtl: trimmed(form.dtl),
		vat: trimmed(form.vat),
		irs: trimmed(form.irs),
		tel: trimmed(form.tel),
		mob: trimmed(form.mob),
		fax: trimmed(form.fax),
		rnl: form.rnl,
		popt: form.popt,
		rpm: form.rpm,
		hidden_reader: form.hidden_reader,
		lang: trimmed(form.lang),
		erpcd: trimmed(form.erpcd),
		erpcd2: trimmed(form.erpcd2),
		erpcd3: trimmed(form.erpcd3),
		erpcd4: trimmed(form.erpcd4),
		erpcd5: trimmed(form.erpcd5),
		img1: form.img1 ?? null,
		imgdel1: form.imgdel1
	} as CustomerToken;
}
